"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { type Mentor, type ExamId, MENTOR_SERVICES, formatRate, getInitials } from "@/lib/mentors";

type ExamFilter = ExamId | "all";
type SortOption = "featured" | "price-asc" | "price-desc";

export function MentorsBrowser({
  mentors,
  defaultExam,
}: {
  mentors: Mentor[];
  defaultExam: ExamId | null;
}) {
  const [exam, setExam] = useState<ExamFilter>(defaultExam ?? "all");
  const [service, setService] = useState<string>("all");
  const [sort, setSort] = useState<SortOption>("featured");

  const visible = useMemo(() => {
    let list = mentors.slice();
    if (exam !== "all") list = list.filter((m) => m.exams.includes(exam));
    if (service !== "all") list = list.filter((m) => m.services.includes(service));

    if (sort === "price-asc") list.sort((a, b) => a.hourlyRate - b.hourlyRate);
    else if (sort === "price-desc") list.sort((a, b) => b.hourlyRate - a.hourlyRate);
    else
      list.sort((a, b) =>
        a.featured !== b.featured ? (a.featured ? -1 : 1) : b.createdAt - a.createdAt,
      );
    return list;
  }, [mentors, exam, service, sort]);

  const selectClass =
    "h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]";

  return (
    <div className="space-y-8">
      {/* Filters / sort */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <label className="flex items-center gap-2 text-sm text-[var(--muted-foreground)]">
          Exam
          <select value={exam} onChange={(e) => setExam(e.target.value as ExamFilter)} className={selectClass}>
            <option value="all">All</option>
            <option value="esat">ESAT</option>
            <option value="tmua">TMUA</option>
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-[var(--muted-foreground)]">
          Service
          <select value={service} onChange={(e) => setService(e.target.value)} className={selectClass}>
            <option value="all">All</option>
            {MENTOR_SERVICES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-[var(--muted-foreground)]">
          Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as SortOption)} className={selectClass}>
            <option value="featured">Featured</option>
            <option value="price-asc">Price: low to high</option>
            <option value="price-desc">Price: high to low</option>
          </select>
        </label>
      </div>

      {visible.length === 0 ? (
        <p className="text-center text-[var(--muted-foreground)] font-polysans py-10">
          No mentors match your filters. Try broadening your search.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((mentor) => (
            <MentorCard key={mentor._id} mentor={mentor} />
          ))}
        </div>
      )}
    </div>
  );
}

function MentorCard({ mentor }: { mentor: Mentor }) {
  return (
    <div className="group flex flex-col rounded-lg border border-[var(--border)] bg-[var(--card)] p-6 hover:border-[var(--primary)] transition-all">
      <div className="flex items-center gap-4">
        {mentor.photoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={mentor.photoUrl}
            alt={mentor.name}
            className="h-16 w-16 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--primary)]/15 text-lg font-semibold text-[var(--primary)]">
            {getInitials(mentor.name)}
          </div>
        )}
        <div className="min-w-0">
          <h2 className="text-lg font-polysans text-[var(--foreground)] truncate">{mentor.name}</h2>
          <p className="text-sm text-[var(--muted-foreground)] truncate">{mentor.university}</p>
          <p className="text-sm text-[var(--muted-foreground)] truncate">{mentor.course}</p>
        </div>
      </div>

      {mentor.specialties.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {mentor.specialties.slice(0, 4).map((s) => (
            <span
              key={s}
              className="rounded-full border border-[var(--border)] px-2 py-0.5 text-xs text-[var(--muted-foreground)]"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between">
        <span className="text-lg font-semibold text-[var(--foreground)]">
          {formatRate(mentor.hourlyRate, mentor.currency)}
        </span>
        <Link
          href={`/mentors/${mentor.slug}`}
          className="rounded-full bg-[var(--primary)] px-4 py-1.5 text-sm text-white shadow-sm hover:shadow-md transition-all"
        >
          View profile
        </Link>
      </div>
    </div>
  );
}
